/*
 * 单个工具调用行（timeline 里的一步）
 *
 * 极简扁平:done 行无任何标记(纯文本悬挂缩进), 仅运行中→loader / 失败→红叉;
 * 不显示耗时. label 形如「读取表格(文件名)」, 点行内任意处展开入参/结果/错误.
 */

import React, { useEffect, useState } from 'react'
import { Progress } from 'antd'
import type { MessagePart } from '../../types'
import { LoaderIcon, XIcon } from './icons'
import {
  extractCommandPreview,
  extractToolArgHint,
  isInternalSkillLoad
} from './toolIcons'
import { getToolName } from '../../settings/toolStatusLabels'
import CodeDetailBlock, { CodeSection } from './CodeDetailBlock'
import { prefetchSkillCache } from './skillCache'
import { store } from '../../../../app/store'
import './ToolCallInline.scss'

type ToolCallPart = Extract<MessagePart, { type: 'tool-call' }>

interface Props {
  part: ToolCallPart
  /** 是否是 timeline 里的最后一步 —— 决定是否画向下的连接线 */
  isLast?: boolean
}

/** 把工具 output 格式化成可展示文本；返回 null = 没有可展示的结果 */
function formatToolOutput(output: unknown): string | null {
  if (output == null) return null
  if (typeof output === 'string') {
    const trimmed = output.trim()
    // OpenAI 路径在后端没下发 output 时会塞占位符 'completed'，不是真实结果 → 不展示
    if (!trimmed || trimmed === 'completed') return null
    return trimmed
  }
  try {
    return JSON.stringify(output, null, 2)
  } catch {
    return String(output)
  }
}

// ⚡ React.memo 默认浅比较 props (part + isLast).
//
// 大量浪费 reconcile 的场景: 父 ToolCallGroup re-render 时 calls.map 会让所有
// ToolCallInline 都跑一次. 但**已完成 (output-available / output-error /
// incomplete) 的 tool**, 它的 part 对象引用永远不变 (useSSEChat 只 update
// in-progress 的 part), 浅比较直接跳过, 这条 inline 完全不动.
//
// iOS 类比: 完工 cell 等同 UITableViewCell prepareForReuse 走完不再 setup,
// 后续 reloadData 触发时 dequeueReusableCell 拿回相同实例直接复用.
const ToolCallInlineImpl: React.FC<Props> = ({ part }) => {
  const [expanded, setExpanded] = useState(false)

  const isRunning =
    part.state === 'input-streaming' || part.state === 'input-available'

  // 懒加载技能 name → display_name 映射；首次渲染时拿不到走 raw slug，
  // prefetch 完成后强制重渲拿到中文名
  const [, forceUpdate] = useState(0)
  useEffect(() => {
    let cancelled = false
    try {
      const state: any = store.getState()
      const group = state?.group
      const groupId = group?.groupId || ''
      if (!groupId) return
      prefetchSkillCache(groupId).then(() => {
        if (!cancelled) forceUpdate((n) => n + 1)
      })
    } catch {
      /* no-op */
    }
    return () => {
      cancelled = true
    }
  }, [])

  // 隐藏：加载内部规则 skill（xhey-shared）对用户没有展示价值
  if (isInternalSkillLoad(part.args)) {
    return null
  }

  // label:「动作名(关键参数)」—— 如"读取表格(黄泥龙…xlsx)"/"执行命令(查询照片)";
  // 动作名走 getToolName(中文友好, 不露 bash/raw slug), 括号里是 extractToolArgHint
  // 取的最能说明意图的 arg(文件名/命令白话/关键词). 状态由左侧 badge 承载.
  const isIncomplete =
    part.state === 'output-error' || part.state === 'incomplete'
  const isCancelled =
    part.state === 'incomplete' && part.cancelReason === 'cancelled'
  const toolDisplayName = getToolName(part.toolName)
  const argHint = extractToolArgHint(part.args)
  const rowLabel = argHint ? `${toolDisplayName}(${argHint})` : toolDisplayName
  // 展开面板三段：入参（args 走 extractCommandPreview 的智能预览）/
  // 结果（output）/ 错误（errorText）。任一有内容即可展开。
  const argsPreview = extractCommandPreview(part.args, toolDisplayName)
  const outputPreview = formatToolOutput(part.output)
  const errorText = part.errorText
  const canExpand =
    argsPreview !== null || outputPreview !== null || !!errorText
  // 进度百分比 — span 文案 + <Progress> 共用一份, 避免算两遍 (CR nit)
  const progressPct =
    part.progress && part.progress.total > 0
      ? Math.min(
          100,
          Math.round((part.progress.done / part.progress.total) * 100)
        )
      : 0

  return (
    <div className="aiClerk-tool" data-tool-call-inline>
      {/* 纯文本顶格(无缩进); 点整行展开详情. 运行中/出错时行首带个小图标, 完成态无标记 */}
      <div
        className={'aiClerk-tool__row' + (canExpand ? ' is-clickable' : '')}
        onClick={() => canExpand && setExpanded((v) => !v)}
        aria-label={
          canExpand ? (expanded ? '收起详情' : '展开详情') : undefined
        }
      >
        {isRunning ? (
          <span className="aiClerk-tool__badge is-running">
            <LoaderIcon className="aiClerk-tool__badge-icon" />
          </span>
        ) : isIncomplete ? (
          <span className="aiClerk-tool__badge is-error">
            <XIcon className="aiClerk-tool__badge-icon" />
          </span>
        ) : null}
        <span className="aiClerk-tool__label-wrap">
          <span
            className={
              'aiClerk-tool__label' +
              (isCancelled ? ' is-cancelled' : '') +
              (isRunning ? ' is-running' : '')
            }
          >
            {rowLabel}
          </span>
        </span>
      </div>
      {part.progress && part.progress.total > 0 && isRunning ? (
        <div
          className="aiClerk-tool__progress"
          style={{ padding: '2px 0 6px 22px' }}
        >
          {/* label 作阶段标识置于进度条上方: 多阶段 (下载图片→写入表格) 共用同一 toolCallId,
              切阶段时 done/total 会 100%→0% 跳变, 靠 label 先变明示是"换阶段"而非回退. */}
          <span style={{ fontSize: 12, color: '#8a8f99' }}>
            {part.progress.label ? `${part.progress.label} ` : ''}
            {part.progress.done}/{part.progress.total} · {progressPct}%
          </span>
          <Progress
            percent={progressPct}
            status="active"
            strokeWidth={6}
            showInfo={false}
          />
        </div>
      ) : null}
      {expanded && (
        <CodeDetailBlock
          sections={
            [
              argsPreview != null ? { label: '入参', body: argsPreview } : null,
              outputPreview != null
                ? { label: '结果', body: outputPreview }
                : null,
              errorText ? { label: '错误', body: errorText } : null
            ].filter(Boolean) as CodeSection[]
          }
        />
      )}
    </div>
  )
}

// 包 memo 出去. 默认浅比较 props.part + props.isLast 引用相等就跳过 body.
export const ToolCallInline = React.memo(ToolCallInlineImpl)
ToolCallInline.displayName = 'ToolCallInline'

export default ToolCallInline
